import { ContentState, EditorState, convertToRaw } from "draft-js";
import React, { useState, useEffect } from "react";
import { Editor } from "react-draft-wysiwyg";
import ImageUploadButton from "../website/ImageUploaderButton";
import useAdminDataStore, { selectCurrentGroupId, selectAllGroups } from "../../../store/adminData/adminData";
import FUpload from "../../commons/formFields/FUpload";
import { Alert, Tooltip } from "antd";
import { ChantInput, ChantSelect, ChantCheckbox, ChantButton } from "../../../CommonComponents";
import draftToHtml from "draftjs-to-html";
import {
  fbSavePartnerDetails,
  getPartnerDetails,
} from "../../../services/firebaseService/endPoints/admin/partners";
import htmlToDraft from "html-to-draftjs";
import {
  getCurrencyUnicode,
  invalidateStadiumCache,
  postUpdateCDNUtil,
} from "../../../helperFunctions/util";
import { verifyStripeAccountId } from "../../../utils/errors";
import { withRouter } from "../../../utils/helper";
import { listFilesInFolder } from "../../../services/general/images";
import PartnerImage from "../../../images/partner-card.png";
import logger from "../../../utils/logger";
import ScreenHeader from "../../../CommonComponents/ScreenHeader/ScreenHeader";

const ProcessingFeeTypes = [
  "Group pays (Fees absorbed by group)",
  "Member pays (Fees added to total)",
  "Ask Member to pay (Request member pay fee but not required)",
];

const MAX_LENGTH = 500;

const isValidWebsiteUrl = (value) => {
  if (!value || typeof value !== "string") return true;
  const trimmed = value.trim();
  if (!trimmed) return true;
  try {
    const url = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

const CreatePartners = (props) => {
  const currentGroup = useAdminDataStore(selectCurrentGroupId);
  const allGroups = useAdminDataStore(selectAllGroups);
  const adminData = allGroups[currentGroup];
  const paymentDetails = adminData?.data?.paymentDetails;

  const [name, setName] = useState("");
  const [partnerWebsite, setPartnerWebsite] = useState("");
  const [regularLogoList, setRegularLogoList] = useState([]);
  const [transparentLogoList, setTransparentLogoList] = useState([]);
  const [regularLogoFile, setRegularLogoFile] = useState({});
  const [transparentLogoFile, setTransparentLogoFile] = useState({});
  const [logoImgType, setLogoImgType] = useState("regular");
  const [details, setDetails] = useState(EditorState.createEmpty());
  const [discounts, setDiscounts] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [qrImage, setQrImage] = useState(null);
  const [collectDonations, setCollectDonations] = useState(false);
  const [suggestedAmounts, setSuggestedAmounts] = useState([]);
  const [displayOn, setDisplayOn] = useState({
    "Website: Partners": false,
    "App: Partners": false,
    "App: Home": false,
  });
  const [processingFee, setProcessingFee] = useState("");
  const [errorInForm, setErrorInForm] = useState({
    isError: false,
    visible: false,
    message: "",
  });
  const [loadingDocument, setLoadingDocument] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(undefined);

  const handleEditMode = () => {
    getPartnerDetails(
      currentGroup,
      props.router.params.id,
    ).then((data) => {
      let partnerData = {};
      partnerData = { ...(data || {}) };

      if (data.details) {
        let blockArray = htmlToDraft(data.details);
        if (blockArray) {
          let contentState = ContentState.createFromBlockArray(blockArray);
          let editorState = EditorState.createWithContent(contentState);
          partnerData.details = editorState;
        } else {
          partnerData.details = EditorState.createEmpty();
        }
      } else {
        partnerData.details = EditorState.createEmpty();
      }
      partnerData.displayOn = (data.displayOn || []).reduce(
        (acc, val) => {
          acc[val] = true;
          return acc;
        },
        {
          "Website: Partners": false,
          "App: Partners": false,
          "App: Home": false,
        },
      );
      partnerData.discounts = data.discounts || data.benefits || "";
      partnerData.discountAmount = data.discountAmount || "";
      partnerData.partnerWebsite = data.partnerWebsite || "";
      partnerData.qrImage = null;
      partnerData.regularLogoList = [];
      partnerData.transparentLogoList = [];
      partnerData.regularLogoFile = {};
      partnerData.transparentLogoFile = {};
      partnerData.logoImgType = data.isLogoTransparent
        ? "transparent"
        : "regular";
      partnerData.galleryImages = [];
      partnerData.galleryFiles = [];
      if (data.showQrImage) {
        partnerData.qrImage = `https://firebasestorage.googleapis.com/v0/b/${import.meta.env.VITE_FIREBASE_STORAGE_BUCKET}/o/${currentGroup}%2Fpartner%2F${props.router.params.id}%2FqrImage?alt=media`;
      }
      if (data.showLogo) {
        const logoUrl = `https://firebasestorage.googleapis.com/v0/b/${import.meta.env.VITE_FIREBASE_STORAGE_BUCKET}/o/${currentGroup}%2Fpartner%2F${props.router.params.id}%2Flogo?alt=media`;
        if (data.isLogoTransparent) {
          partnerData.transparentLogoList = [{ uid: "-1", url: logoUrl }];
        } else {
          partnerData.regularLogoList = [{ uid: "-1", url: logoUrl }];
        }
      }

      // Get all actual files in the gallery folder instead of using hardcoded URLs
      const galleryFolderPath = `${currentGroup}/partner/${props.router.params.id}/gallery`;
      listFilesInFolder(galleryFolderPath)
        .then((galleryFilesResult) => {
          // Store both file objects (for delete operations) and URLs (for display)
          partnerData.galleryFiles = galleryFilesResult;
          partnerData.galleryImages = galleryFilesResult.map((file) => file.url);
          setName(partnerData.name !== undefined ? partnerData.name : "");
          setPartnerWebsite(partnerData.partnerWebsite || "");
          setRegularLogoList(partnerData.regularLogoList || []);
          setTransparentLogoList(partnerData.transparentLogoList || []);
          setRegularLogoFile(partnerData.regularLogoFile || {});
          setTransparentLogoFile(partnerData.transparentLogoFile || {});
          setLogoImgType(partnerData.logoImgType || "regular");
          setDetails(partnerData.details || EditorState.createEmpty());
          setDiscounts(partnerData.discounts || "");
          setDiscountAmount(partnerData.discountAmount || "");
          setGalleryImages(partnerData.galleryImages || []);
          setGalleryFiles(partnerData.galleryFiles || []);
          setQrImage(partnerData.qrImage || null);
          setCollectDonations(partnerData.collectDonations || false);
          setSuggestedAmounts(partnerData.suggestedAmounts || []);
          setDisplayOn(partnerData.displayOn || {
            "Website: Partners": false,
            "App: Partners": false,
            "App: Home": false,
          });
          setProcessingFee(partnerData.processingFee || "");
          if (partnerData.status !== undefined) setStatus(partnerData.status);
          setLoadingDocument(false);
        })
        .catch((error) => {
          console.error("Error loading gallery images:", error);
          // Fallback to empty arrays if there's an error
          partnerData.galleryFiles = [];
          partnerData.galleryImages = [];
          setName(partnerData.name !== undefined ? partnerData.name : "");
          setPartnerWebsite(partnerData.partnerWebsite || "");
          setRegularLogoList(partnerData.regularLogoList || []);
          setTransparentLogoList(partnerData.transparentLogoList || []);
          setRegularLogoFile(partnerData.regularLogoFile || {});
          setTransparentLogoFile(partnerData.transparentLogoFile || {});
          setLogoImgType(partnerData.logoImgType || "regular");
          setDetails(partnerData.details || EditorState.createEmpty());
          setDiscounts(partnerData.discounts || "");
          setDiscountAmount(partnerData.discountAmount || "");
          setGalleryImages([]);
          setGalleryFiles([]);
          setQrImage(partnerData.qrImage || null);
          setCollectDonations(partnerData.collectDonations || false);
          setSuggestedAmounts(partnerData.suggestedAmounts || []);
          setDisplayOn(partnerData.displayOn || {
            "Website: Partners": false,
            "App: Partners": false,
            "App: Home": false,
          });
          setProcessingFee(partnerData.processingFee || "");
          if (partnerData.status !== undefined) setStatus(partnerData.status);
          setLoadingDocument(false);
        });
    });
  };

  useEffect(() => {
    if (props.router.params.id) {
      handleEditMode();
    } else {
      setLoadingDocument(false);
    }
  }, [currentGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOptionChange = (e) => {
    const { name: fieldName, value } = e.target;
    if (fieldName === "status") setStatus(value);
    else if (fieldName === "processingFee") setProcessingFee(value);
  };

  const handleInputChange = (e) => {
    const fieldName = e.target.name;
    const value = e.target.value;
    if (fieldName === "name") setName(value);
    else if (fieldName === "partnerWebsite") setPartnerWebsite(value);
    else if (fieldName === "discounts") setDiscounts(value);
    else if (fieldName === "discountAmount") setDiscountAmount(value);
    else if (fieldName === "suggestedAmounts") setSuggestedAmounts(value);
    else if (fieldName === "processingFee") setProcessingFee(value);
  };

  const handleEditorStateChange = (state) => {
    setDetails(state);
  };

  const handleLogoTypeChange = (val) => {
    setLogoImgType(val);
  };

  const uploadLogoAction = (file) => {
    if (logoImgType === "regular") {
      setRegularLogoFile(file);
    } else {
      setTransparentLogoFile(file);
    }
  };

  const handleLogoImageChange = ({ fileList }) => {
    if (logoImgType === "regular") {
      if (fileList.length === 0) {
        setRegularLogoFile({});
      } else {
        setRegularLogoFile(fileList[0].originFileObj);
      }
      setRegularLogoList(fileList);
    } else {
      if (fileList.length === 0) {
        setTransparentLogoFile({});
      } else {
        setTransparentLogoFile(fileList[0].originFileObj);
      }
      setTransparentLogoList(fileList);
    }
  };

  const handleSubmit = () => {
    try {
      if (
        collectDonations &&
        !verifyStripeAccountId(
          adminData?.data?.paymentDetails?.stripeAccountId,
        )
      ) {
        return;
      }

      setSaving(true);
      if (
        collectDonations &&
        !(paymentDetails || {}).stripeAccountId
      ) {
        alert(
          "Must have Stripe account through Chant to collect donations. Request from support@chant.fan.",
        );
        return;
      }

      let data = {
        name,
        partnerWebsite,
        regularLogoList,
        transparentLogoList,
        regularLogoFile,
        transparentLogoFile,
        logoImgType,
        details,
        discounts,
        discountAmount,
        galleryImages,
        galleryFiles,
        qrImage,
        collectDonations,
        suggestedAmounts,
        displayOn,
        processingFee,
        errorInForm,
        loadingDocument,
        saving,
        status,
      };

      const errors = [];

      const currentLogoFile =
        data.logoImgType === "transparent"
          ? data.transparentLogoFile
          : data.regularLogoFile;
      const currentLogoList =
        data.logoImgType === "transparent"
          ? data.transparentLogoList
          : data.regularLogoList;

      if (!data.name) errors.push("Please enter your full name");
      if (data.partnerWebsite && !isValidWebsiteUrl(data.partnerWebsite)) {
        errors.push(
          "Please enter a valid website URL (e.g. https://example.com)",
        );
      }
      if (!currentLogoFile?.name && currentLogoList.length === 0)
        errors.push("Please add a logo");
      if (!data.details.getCurrentContent().getPlainText(""))
        errors.push("Please enter details");

      if (
        data.collectDonations &&
        data.suggestedAmounts.filter((amount) => amount && amount.toString())
          .length < 1
      ) {
        errors.push("Please enter donation amounts");
      }

      if (
        data.collectDonations &&
        (!data.processingFee || data.processingFee === "")
      ) {
        errors.push("Please select processing fee");
      }

      if (errors.length > 0) {
        setErrorInForm({
          isError: true,
          visible: true,
          message: errors[0],
        });
        setSaving(false);
        window.scrollTo(0, 0);
        return;
      }

      data.details = draftToHtml(
        convertToRaw(data.details.getCurrentContent()),
      );
      data.displayOn = Object.keys(data.displayOn).filter(
        (key) => data.displayOn[key],
      );
      data.sortIndex = -1;

      data.isLogoTransparent = logoImgType === "transparent";

      const finalLogoFile =
        logoImgType === "transparent"
          ? transparentLogoFile
          : regularLogoFile;

      if (finalLogoFile?.name) {
        data.logo = finalLogoFile;
      } else {
        const existingLogoList =
          logoImgType === "transparent"
            ? transparentLogoList
            : regularLogoList;
        data.logo = existingLogoList[0]?.url || null;
      }

      let promise = props.router.params.id
        ? fbSavePartnerDetails(
            data,
            currentGroup,
            props.router.params.id,
          )
        : fbSavePartnerDetails(data, currentGroup);
      promise.then(() => {
        postUpdateCDNUtil(currentGroup);
        invalidateStadiumCache(adminData?.data?.groupKey);
        props.router.navigate("/admin/partners/list");
      });
    } catch (error) {
      logger.error(error);
    } finally {
      setSaving(false);
    }
  };

  const _getLengthOfSelectedText = () => {
    const currentSelection = details.getSelection();
    const isCollapsed = currentSelection.isCollapsed();

    let length = 0;

    if (!isCollapsed) {
      const currentContent = details.getCurrentContent();
      const startKey = currentSelection.getStartKey();
      const endKey = currentSelection.getEndKey();
      const startBlock = currentContent.getBlockForKey(startKey);
      const isStartAndEndBlockAreTheSame = startKey === endKey;
      const startBlockTextLength = startBlock.getLength();
      const startSelectedTextLength =
        startBlockTextLength - currentSelection.getStartOffset();
      const endSelectedTextLength = currentSelection.getEndOffset();
      const keyAfterEnd = currentContent.getKeyAfter(endKey);
      if (isStartAndEndBlockAreTheSame) {
        length +=
          currentSelection.getEndOffset() - currentSelection.getStartOffset();
      } else {
        let currentKey = startKey;

        while (currentKey && currentKey !== keyAfterEnd) {
          if (currentKey === startKey) {
            length += startSelectedTextLength + 1;
          } else if (currentKey === endKey) {
            length += endSelectedTextLength;
          } else {
            length += currentContent.getBlockForKey(currentKey).getLength() + 1;
          }

          currentKey = currentContent.getKeyAfter(currentKey);
        }
      }
    }

    return length;
  };

  const _handleBeforeInput = () => {
    const currentContent = details.getCurrentContent();
    const currentContentLength = currentContent.getPlainText("").length;
    const selectedTextLength = _getLengthOfSelectedText();

    if (currentContentLength - selectedTextLength > MAX_LENGTH - 1) {
      window.alert("you can type max 500 characters");

      return "handled";
    }
  };

  const _handlePastedText = (pastedText) => {
    const currentContent = details.getCurrentContent();
    const currentContentLength = currentContent.getPlainText("").length;
    const selectedTextLength = _getLengthOfSelectedText();

    if (
      currentContentLength + pastedText.length - selectedTextLength >
      MAX_LENGTH
    ) {
      window.alert("you can type max 500 characters");

      return "handled";
    }
  };

  let primaryColor = "";
  if (
    adminData.data.configuration &&
    adminData.data.configuration.primaryColor
  ) {
    let { r, g, b } = adminData.data.configuration.primaryColor;
    primaryColor = `rgb(${r}, ${g}, ${b})`;
  }
  return (
    <div className="col">
      {errorInForm.isError && errorInForm.visible && (
        <div className="mx-5 mt-2">
          <Alert
            message="Error"
            description={errorInForm.message}
            type="error"
            closable
            // className="mb-3"
            afterClose={undefined}
          />
        </div>
      )}
      <div className="md-5 border-box" style={{ padding: "12px" }}>
        <ScreenHeader title="Add Partner" />
        <div
          className="p-3 px-5"
          style={{
            backgroundColor: "#ffffff",
            marginTop: "2px",
          }}
        >
          <div className="chant-form-body">
          <ChantSelect
            label="Status"
            value={status}
            onChange={(value) => setStatus(value)}
            options={["Active", "Inactive"].map((v) => ({ value: v, label: v }))}
            placeholder="--Select status--"
            tabIndex="1"
            required
          />
          <ChantInput
            value={name}
            label="Name"
            onChange={handleInputChange}
            name="name"
            placeholder="Enter Partner Name"
            tabIndex="2"
            maxLength={30}
            required
          />
          <ChantInput
            value={partnerWebsite}
            label="Partner Website"
            onChange={handleInputChange}
            name="partnerWebsite"
            placeholder="https://example.com"
            tabIndex="2"
          />
          <div style={{ marginBottom: "16px" }}>
            <label>
              Partner Logo<span style={{ color: "#ff0000" }}>*</span>
            </label>
            <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
              <div>
                <p style={{ fontWeight: "bold", marginBottom: "8px" }}>
                  Image Type:
                </p>
                <ChantSelect
                  popupMatchSelectWidth={false}
                  options={[
                    { value: "regular", label: "Regular (JPG)" },
                    { value: "transparent", label: "Transparent (PNG)" },
                  ]}
                  value={logoImgType}
                  onChange={handleLogoTypeChange}
                  style={{ width: 180 }}
                />
              </div>
              <FUpload
                aspectRatio={1 / 1}
                showCropOptions={logoImgType === "regular"}
                action={uploadLogoAction}
                accept={
                  logoImgType === "regular"
                    ? ".jpg,.jpeg,.png"
                    : ".png"
                }
                fileList={
                  logoImgType === "regular"
                    ? regularLogoList
                    : transparentLogoList
                }
                onChange={handleLogoImageChange}
              />
            </div>
          </div>
          <div style={{ marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px" }}>
              <label style={{ marginBottom: 0 }}>Partner Images</label>
              <Tooltip
                placement="topLeft"
                title={() => (
                  <div
                    style={{
                      height: "fit-content",
                      width: "400px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    Image dimensions are 800x450 (16:9 ratio).
                  </div>
                )}
              >
                <span className="border-0">
                  &nbsp;<i className="fa fa-question-circle-o"></i>
                </span>
              </Tooltip>
            </div>
            <div style={{ display: "flex", gap: 5 }}>
              {Array.from({ length: 5 }, (_, index) => {
                return (
                  <div className="mx-2" key={index}>
                    <ImageUploadButton
                      text={`Upload #${index + 1}`}
                      images={[galleryImages[index]]}
                      aspectRatio={16 / 9}
                      onSave={(images) => {
                        let newGalleryImages = galleryImages;

                        if (!images[0]) {
                          newGalleryImages[index] = null;
                        } else {
                          newGalleryImages[index] = images[0];
                        }
                        setGalleryImages(newGalleryImages);
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px" }}>
              <label style={{ marginBottom: 0 }}>
                Details<span style={{ color: "#ff0000" }}>*</span>
              </label>
              <Tooltip
                placement="topLeft"
                title={() => (
                  <div
                    style={{
                      height: "fit-content",
                      width: "200px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    Max 500 characters
                  </div>
                )}
              >
                <span className="border-0">
                  &nbsp;<i className="fa fa-question-circle-o"></i>
                </span>
              </Tooltip>
            </div>
            <div
              style={{
                border: "1px solid #ced4da",
                borderRadius: 5,
              }}
            >
              <Editor
                maxLength={500}
                editorState={
                  typeof details === "object"
                    ? details
                    : undefined
                }
                onEditorStateChange={(state) =>
                  handleEditorStateChange(state)
                }
                toolbar={{
                  options: ["inline", "list", "link"],
                  inline: {
                    inDropdown: false,
                    options: ["bold", "italic", "underline"],
                  },
                  list: {
                    inDropdown: false,
                    options: ["unordered", "ordered"],
                  },
                  link: {
                    inDropdown: false,
                    defaultTargetOption: "_self",
                    options: ["link", "unlink"],
                    linkCallback: undefined,
                  },
                }}
                stripPastedStyles={true}
                handleBeforeInput={_handleBeforeInput}
                handlePastedText={_handlePastedText}
                tabIndex="2"
              />
            </div>
          </div>
          <div style={{ marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px" }}>
              <label style={{ marginBottom: 0 }}>Discount Amount</label>
              <Tooltip
                placement="topLeft"
                title={() => (
                  <div
                    style={{
                      height: "fit-content",
                      width: "400px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <img src={PartnerImage} alt="" />
                  </div>
                )}
              >
                <span className="border-0">
                  <i className="fa fa-question-circle-o" />
                </span>
              </Tooltip>
            </div>
            <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
              <ChantInput
                value={discountAmount}
                onChange={handleInputChange}
                name="discountAmount"
                maxLength={4}
                placeholder="Ex. 10% or $10"
                tabIndex="2"
                style={{ width: 160 }}
              />
              <span className="form-text text-muted">
                (Optional) Add discount amount as percent or currency (ex:
                $10, 15%)
              </span>
            </div>
          </div>
          <div style={{ marginBottom: "16px" }}>
            <label>Discount Detail</label>
            <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
              <ChantInput
                value={discounts}
                onChange={handleInputChange}
                name="discounts"
                maxLength={150}
                tabIndex="2"
                style={{ width: 160 }}
              />
              <span className="form-text text-muted">
                (Optional) Add brief discount summary. Will be appended to
                details. Max 150 characters
              </span>
            </div>
          </div>
          <div style={{ marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px" }}>
              <label style={{ marginBottom: 0 }}>QR code</label>
              <Tooltip
                placement="topLeft"
                title={() => (
                  <div
                    style={{
                      height: "fit-content",
                      width: "400px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    Upload QR code if provided by partner.
                  </div>
                )}
              >
                <span className="border-0">
                  &nbsp;<i className="fa fa-question-circle-o"></i>
                </span>
              </Tooltip>
            </div>
            <ImageUploadButton
              text="Upload"
              aspectRatio={1}
              images={[qrImage]}
              onSave={(images) => {
                let newQrImage = images[0];
                setQrImage(newQrImage);
              }}
            />
          </div>
          <div style={{ marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px" }}>
              <label style={{ marginBottom: 0 }}>Display partner on</label>
              <Tooltip
                placement="topLeft"
                title={() => (
                  <div
                    style={{
                      height: "fit-content",
                      width: "400px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    Partners appear under More / Partners. Select one partner
                    to feature in the app home feed.
                  </div>
                )}
              >
                <span className="border-0">
                  &nbsp;<i className="fa fa-question-circle-o"></i>
                </span>
              </Tooltip>
            </div>
            <div className="d-flex" style={{ gap: 50 }}>
              <ChantCheckbox
                checked={displayOn["App: Partners"]}
                onChange={(e) => {
                  let newDisplayOn = displayOn;
                  newDisplayOn["App: Partners"] = e.target.checked;
                  setDisplayOn({ ...newDisplayOn });
                }}
              >
                App: Partners
              </ChantCheckbox>
              <ChantCheckbox
                checked={displayOn["App: Home"]}
                onChange={(e) => {
                  let newDisplayOn = displayOn;
                  newDisplayOn["App: Home"] = e.target.checked;
                  setDisplayOn({ ...newDisplayOn });
                }}
              >
                App: Home
              </ChantCheckbox>
              <ChantCheckbox
                checked={displayOn["Website: Partners"]}
                onChange={(e) => {
                  let newDisplayOn = displayOn;
                  newDisplayOn["Website: Partners"] = e.target.checked;
                  setDisplayOn({ ...newDisplayOn });
                }}
              >
                Website: Partners
              </ChantCheckbox>
            </div>
          </div>
          <div style={{ marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px" }}>
              <label style={{ marginBottom: 0 }}>Collect donations?</label>
              <Tooltip
                placement="topLeft"
                title={() => (
                  <div
                    style={{
                      height: "fit-content",
                      width: "400px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    Stripe account required. Request from support@stripe.com.
                  </div>
                )}
              >
                <span className="border-0">
                  &nbsp;<i className="fa fa-question-circle-o"></i>
                </span>
              </Tooltip>
            </div>
            <ChantCheckbox
              checked={collectDonations}
              onChange={(e) => setCollectDonations(e.target.checked)}
            >
              <span style={{ fontSize: 12 }}>Stripe account required.</span>
            </ChantCheckbox>
          </div>

          {collectDonations && (
            <div style={{ marginBottom: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px" }}>
                <label style={{ marginBottom: 0 }}>
                  Suggested amounts({getCurrencyUnicode((paymentDetails || {}).currency)})
                </label>
                <Tooltip
                  placement="topLeft"
                  title={() => (
                    <div
                      style={{
                        height: "fit-content",
                        width: "400px",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      Enter values for suggested donation amounts. A fourth
                      option will appear automatically allowing members to
                      donate a custom amount.
                    </div>
                  )}
                >
                  <span className="border-0">
                    &nbsp;<i className="fa fa-question-circle-o"></i>
                  </span>
                </Tooltip>
              </div>
              <div className="d-flex" style={{ gap: 40 }}>
                <ChantInput
                  value={suggestedAmounts[0]}
                  onChange={(e) => {
                    if (e.target.value.length > 4) return;
                    e.preventDefault();
                    let newSuggestedAmounts = suggestedAmounts;
                    if (Number(e.target.value) > 0) {
                      newSuggestedAmounts[0] =
                        e.target.value.indexOf(".") !== -1
                          ? Number(e.target.value.slice(0, e.target.value.indexOf(".") + 3))
                          : Number(e.target.value);
                    } else {
                      newSuggestedAmounts[0] = "";
                    }
                    handleInputChange({ target: { name: "suggestedAmounts", value: newSuggestedAmounts } });
                  }}
                  name="suggestedAmounts"
                  tabIndex="2"
                  style={{ width: 100 }}
                />

                {[1, 2, 3].map((idx) => (
                  <ChantInput
                    key={idx}
                    value={suggestedAmounts[idx]}
                    onChange={(e) => {
                      if (e.target.value.length > 4) return;
                      let newSuggestedAmounts = suggestedAmounts;
                      newSuggestedAmounts[idx] =
                        e.target.value.indexOf(".") !== -1
                          ? Number(e.target.value.slice(0, e.target.value.indexOf(".") + 3))
                          : Number(e.target.value);
                      if (newSuggestedAmounts[idx] === 0) {
                        newSuggestedAmounts[idx] = "";
                      }
                      handleInputChange({ target: { name: "suggestedAmounts", value: newSuggestedAmounts } });
                    }}
                    name="suggestedAmounts"
                    tabIndex="2"
                    style={{ width: 100 }}
                  />
                ))}
              </div>
            </div>
          )}

          {collectDonations && (
            <div style={{ marginBottom: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px" }}>
                  <label style={{ marginBottom: 0 }}>Processing Fee{collectDonations && <span style={{ color: "red" }}>*</span>}</label>
                  <Tooltip
                    title={
                      <div>
                        <p style={{ fontWeight: "bold" }}>Fees</p>
                        <p style={{ marginBottom: 0 }}>Stripe card processing fee: 2.9% + $0.30 (can vary)</p>
                        <p>Chant fee: 3%</p>
                        <p style={{ fontWeight: "bold" }}>Member Pay</p>
                        <p>Stripe collects the fee on the entire purchase amount. So, if a member covers fees that is added to the total charged ($10 item + $0.59 fee paid by member for $10.59) stripe will collect a fee on the entire $10.59.</p>
                        <p>To account for this we add 3% + $.30 (.1% more) to the original item price for Stripe so your net is close to the item price - it may be off very slightly.</p>
                        <p>Stripe fees can vary based on member payment method but are not known until after the transaction is complete (foreign card, etc). Your net proceeds may not cover the full item amount but should be close.</p>
                        <p style={{ fontWeight: "bold" }}>Member Pay (legal Limitations)</p>
                        <p>Not all states/countries allow the fee to be passed to the member. Chant is not responsible for determining your local laws. Please confirm this for your location.</p>
                      </div>
                    }
                  >
                    <span className="border-0">
                      &nbsp;<i className="fa fa-question-circle-o"></i>
                    </span>
                  </Tooltip>
                </div>
              <ChantSelect
                value={processingFee}
                onChange={(value) => setProcessingFee(value)}
                options={ProcessingFeeTypes.map((v) => ({ value: v, label: v }))}
                placeholder="--Select--"
                tabIndex="5"
                helpText="Stripe (2.9% + $.030) + Chant (3%). See '?' for additional details."
              />
            </div>
          )}

          </div>
          <div className="mt-3 d-flex justify-content-center">
            <ChantButton
              tabIndex="14"
              style={{ padding: "3px 30px" }}
              loading={saving}
              onClick={handleSubmit}
            >
              Submit
            </ChantButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(CreatePartners);
